import * as React from 'react';
import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useSocket } from '../contexts/SocketContext';
import { webrtcService } from '../utils/webrtc';
import { logger } from '../utils/logger';

interface WebRTCContextType {
  socket: any;
  isConnected: boolean;
  webrtcService: typeof webrtcService;
}

const WebRTCContext = createContext<WebRTCContextType | null>(null);

export const useWebRTC = () => {
  const context = useContext(WebRTCContext);
  if (!context) {
    throw new Error('useWebRTC must be used within WebRTCProvider');
  }
  return context;
};

export const WebRTCProvider = ({ children }: { children: React.ReactNode }) => {
  const { socket, isConnected } = useSocket();

  // Set socket for WebRTC service whenever it changes
  useEffect(() => {
    logger.log('📞 [WebRTCProvider] Socket state changed:', {
      hasSocket: !!socket,
      isConnected,
      socketId: socket?.id
    });
    
    if (socket && isConnected) {
      logger.log('📞 [WebRTCProvider] Setting socket for WebRTC service:', socket.id);
      webrtcService.setSocket(socket);
    } else if (socket && !isConnected) {
      logger.log('⚠️  [WebRTCProvider] Socket exists but not connected yet');
    } else if (!socket) {
      logger.log('⚠️  [WebRTCProvider] No socket available');
    }
  }, [socket, isConnected]);

  return (
    <WebRTCContext.Provider value={{ socket, isConnected, webrtcService }}>
      {children}
    </WebRTCContext.Provider>
  );
};